import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../context/DataContext';
import { useLiveRates } from '../../hooks/useLiveRates';
import { Grid, List, Sparkles, RefreshCw } from 'lucide-react';
import ProductGrid from './components/ProductGrid';
import ProductFilters from './components/ProductFilters';
import './Products.css';

const Products = () => {
  const { products: staticProducts } = useData();
  const { rates: liveRates, loading: ratesLoading, refreshRates } = useLiveRates();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [products, setProducts] = useState([]);

  // Memoize silver products to prevent recreation on every render
  const silverProducts = useMemo(() => {
    return staticProducts.filter(
      product => product.category === 'silver' || product.metal === 'Silver'
    );
  }, [staticProducts]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update products with live rates - using useCallback for stability
  const updateProductsWithLiveRates = useCallback(() => {
    if (silverProducts.length === 0) {
      if (staticProducts.length > 0) {
        setProducts(staticProducts);
      }
      return;
    }

    if (liveRates.length === 0) {
      if (staticProducts.length > 0) {
        setProducts(staticProducts);
      }
      return;
    }

    const updatedProducts = silverProducts.map(product => {
      // Check if it's a silver product
      if (product.category === 'silver') {
        // Find matching silver rate
        const silverRate = liveRates.find(
          rate => rate.metal === 'Silver' || rate.name?.toLowerCase().includes('silver')
        );

        if (silverRate) {
          // Extract weight from product.weight (e.g., "1 kg" -> 1)
          const weightMatch = product.weight.match(/(\d+\.?\d*)\s*(kg|g|gm)/i);
          let weightInGrams = 0;

          if (weightMatch) {
            const weightValue = parseFloat(weightMatch[1]);
            const unit = weightMatch[2].toLowerCase();

            if (unit === 'kg') {
              weightInGrams = weightValue * 1000;
            } else if (unit === 'g' || unit === 'gm') {
              weightInGrams = weightValue;
            }
          }

          // Get silver price per gram from live rate
          let silverPricePerGram = silverRate.price / 1000; // Convert from kg to gram
          // If price is already in grams, use as is
          if (silverRate.unit === 'g' || silverRate.unit === 'gm') {
            silverPricePerGram = silverRate.price;
          }

          // Calculate premium per gram (e.g., ₹3 per gram)
          const premiumPerGram = product.premiumPerGram || 3;
          const totalPricePerGram = silverPricePerGram + premiumPerGram;
          const calculatedPrice = totalPricePerGram * weightInGrams;

          return {
            ...product,
            price: Math.round(calculatedPrice),
            livePrice: silverRate.price,
            premiumPerGram: premiumPerGram,
            silverPricePerGram: silverPricePerGram,
            totalPricePerGram: totalPricePerGram,
            isLive: true,
            lastUpdated: new Date().toLocaleTimeString(),
            priceChange: silverRate.change || 0
          };
        }
      }
      return product;
    });

    setProducts(updatedProducts);
  }, [silverProducts, liveRates, staticProducts]);

  // Update products when dependencies change
  useEffect(() => {
    updateProductsWithLiveRates();
  }, [updateProductsWithLiveRates]);

  // Categories
  const categories = useMemo(() => [
    { id: 'silver', label: 'Silver', icon: '🥈' },
  ], []);

  // Filter products based on category and search
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.weight.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.purity.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [products, selectedCategory, searchQuery]);

  // Page animation variants
  const pageVariants = useMemo(() => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  }), []);

  // Handle view mode toggle
  const toggleViewMode = useCallback(() => {
    setViewMode(prev => prev === 'grid' ? 'list' : 'grid');
  }, []);

  // Handle category change
  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  // Handle search change
  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  return (
    <motion.div
      className="products-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4 }}
    >
      <div className="container-custom">
        <motion.div
          className="products-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="header-content">
            <h1 className="products-title">
              <Sparkles className="title-icon" />
              Our Products
            </h1>
            <p className="products-subtitle">
              Choose from our wide range of silver bullion products
            </p>
          </div>
          <div className="header-actions">
            <motion.button
              className="refresh-btn"
              onClick={refreshRates}
              whileHover={{ scale: 1.05, rotate: 180 }}
              whileTap={{ scale: 0.95 }}
              disabled={ratesLoading}
              aria-label="Refresh rates"
            >
              <RefreshCw size={18} className={ratesLoading ? 'spinning' : ''} />
            </motion.button>
            <motion.button
              className="view-toggle"
              onClick={toggleViewMode}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Toggle view"
            >
              {viewMode === 'grid' ? <List size={20} /> : <Grid size={20} />}
            </motion.button>
          </div>
        </motion.div>

        <ProductFilters
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          isFilterOpen={isFilterOpen}
          setIsFilterOpen={setIsFilterOpen}
        />

        {ratesLoading && (
          <div className="rates-loading-banner">
            <span className="loading-spinner-small" />
            Updating live rates...
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={`${selectedCategory}-${searchQuery}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ProductGrid
              products={filteredProducts}
              viewMode={viewMode}
              liveRates={liveRates}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default Products;